import {useState} from 'react'
import PickupTime from '@/customComponents/sharedComponents/PickupTime.jsx'
import {useParams, useNavigate} from 'react-router'
import {Popover, PopoverButton, PopoverPanel} from '@headlessui/react'
import {
    carIcon, whiteCar, locationIcon, pencilIconBig,
    cardIcon, greenTick, cancelIcon, closeRed,
    personCarryingBag, childCarIcon, luggageIcon, rightArrow, arrowRightWhite, driverPlaceHolder, plusIconLight,
    planeArrival
} from '@img/index.js'
import identityVarified from '@/assets/images/identity-varified.png'
import DriverArrivedBadge from '@/customComponents/sharedComponents/DriverArrivedBadge.jsx'
import TrackDriverButton from '@/customComponents/sharedComponents/TrackDriverButton.jsx'
import DriverContactButtons from '@/customComponents/sharedComponents/DriverContactButtons.jsx'
import RideTabNav from '@/customComponents/uiComponents/global/RideTabNav.jsx'
import {useTripDetails, categoryForStatus} from '@/hooks/useTrips'
import TripRouteMap from '@/components/map/TripRouteMap.jsx'
import {
    locationLabel, formatTime, formatDateTime, formatTravelTime,
    formatPrice, sumQuantity, parseCard, getStatusStyle, getStatusLabel,
    isDriverAssigned, isDriverRevealed, isTripUnderway, tripCancelPolicy
} from '@/utils/functions'
import {parseAppZoneDate, addMinutesToAppZone} from '@/utils/dateUtils'
import CardBrand from '@/customComponents/uiComponents/global/CardBrand.jsx'
import CompletedRideDetails from './CompletedRideDetails.jsx'
import CancelRide from '@/customComponents/uiComponents/global/CancelRide.jsx'
import PoliciesPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PoliciesPopup.jsx'
import RetryHoldButtons from '@/customComponents/RideView/RetryHoldButtons.jsx'
import AlertMessage from '@/customComponents/homeComponents/AlertMessage.jsx'
import {CancellationModificationRefundContent} from '@/customComponents/BookRideSteps/Components/cancellationModificationRefund.jsx'
import {tripCmrPolicy} from '@/customComponents/globalComponents/policyValues.js'
import BlockedTooltip from '@/customComponents/uiComponents/global/BlockedTooltip.jsx'

const formatCents = (cents) => formatPrice(cents, {decimals: 2})

/**
 * One label/value line in the Flight card.
 *
 * ⚠️ AN EMPTY VALUE RENDERS NOTHING, ROW AND ALL. Every field on `trip.flight` is null
 * until the backend's first poll of that flight, so a card that kept its rows would open
 * as a column of labels against blank space — which reads as broken rather than as
 * "not known yet". The card says that in words instead; see `flightPending`.
 */
function FlightRow({label, value}) {
    if (value === null || value === undefined || value === '') return null
    return (
        <div className="flex items-baseline justify-between gap-3 max-md:text-sm">
            <span className="text-(--text-secondary)">{label}</span>
            <span className="text-right font-medium">{value}</span>
        </div>
    )
}

export default function RideDetails() {
    const {rideId} = useParams()
    const navigate = useNavigate()
    const {data, isLoading} = useTripDetails(rideId, {live: true})
    const [showPolicies, setShowPolicies] = useState(false)

    const trip = data?.trip
    const activeCategory = categoryForStatus(trip?.status)

    if (isLoading) {
        return (
            <div className="flex flex-col gap-5">
                <RideTabNav activeCategory={activeCategory}/>
                <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                    <div className="animate-pulse space-y-4">
                        <div className="h-6 bg-gray-200 rounded w-48"></div>
                        <div className="h-64 bg-gray-200 rounded-2xl"></div>
                        <div className="h-20 bg-gray-200 rounded-2xl"></div>
                    </div>
                </div>
            </div>
        )
    }

    if (!trip) {
        return (
            <div className="flex flex-col gap-5">
                <RideTabNav activeCategory={activeCategory}/>
                <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                    <p className="text-(--text-secondary) mb-0!">Trip not found</p>
                </div>
            </div>
        )
    }

    if (trip.status === 'Completed') {
        return (
            <div className="flex flex-col gap-5">
                <RideTabNav activeCategory={activeCategory}/>
                <CompletedRideDetails trip={trip} rideId={rideId}/>
            </div>
        )
    }

    const isCancelled = ['Cancelled', 'NoShow'].includes(trip.status)
    const dimmed = isCancelled ? 'opacity-60' : ''
    // Shared gate — also blocks once the ride is under way (see tripCancelPolicy).
    const {canCancel, blockedReason: cancelBlockedReason} = tripCancelPolicy(trip)
    // An initial trip that already spawned a real (non-Draft) return booking can't get another.
    const hasReturnBooking = !!trip.return_booking && trip.return_booking.status !== 'Draft'

    const durationMin = trip.route?.duration_minutes
    const estimatedArrival = trip.scheduled_at && durationMin
        ? formatTime(addMinutesToAppZone(trip.scheduled_at, durationMin))
        : '--'
    const polyline = trip.route?.polyline
    const legs = trip.route?.legs || []
    const stopCount = trip.stop_overs?.length || 0
    // Real instant, so the leg-end times below render in the viewer's zone like every
    // other trip time (a naive `new Date` here would keep them on the Toronto clock).
    const baseMs = parseAppZoneDate(trip.scheduled_at)?.getTime() ?? null
    const legEnds = []
    if (baseMs) {
        let cursor = baseMs
        if (legs.length > 0) {
            for (const leg of legs) {
                const dur = leg.duration_minutes ?? leg.duration_in_minutes ?? (leg.duration_seconds ? Math.round(leg.duration_seconds / 60) : 0)
                cursor += dur * 60000
                legEnds.push({time: cursor, durationMin: dur})
            }
        } else if (stopCount > 0 && durationMin > 0) {
            const perLeg = Math.round(durationMin / (stopCount + 1))
            for (let i = 0; i < stopCount + 1; i++) {
                cursor += perLeg * 60000
                legEnds.push({time: cursor, durationMin: perLeg})
            }
        }
    }
    const lastLegMin = legEnds.length > 0 ? legEnds[legEnds.length - 1].durationMin : durationMin
    const pricing = trip.selected_vehicle?.pricing
    const pricingBreakdown = trip.selected_vehicle?.pricing_breakdown
    const totalPrice = trip.selected_vehicle?.total_price
    const passengerCount = trip.passenger_count || sumQuantity(trip.selected_passengers)
    const childSeatCount = sumQuantity(trip.selected_child_seats)
    const luggageCount = sumQuantity(trip.selected_luggage)
    const paymentMethod = trip.selected_payment_method
    const {brand: pmBrand, last4: pmLast4} = parseCard(paymentMethod)
    const paymentHold = trip.payment_hold
    const isAuthorized = paymentHold?.is_authorized
    const isCaptured = paymentHold?.is_captured
    const paymentNotAuthorized = isAuthorized === false || isCaptured === false
    const hasCard = Boolean(trip.card_hold_amount_cents)
    const hasWallet = (trip.wallet_amount_cents ?? 0) > 0
    const promoSaved = pricing?.discount_code_discount || 0

    /* The tracked flight, when the trip has one.
       ⚠️ `trip.flight` IS NOT IN api-docs, which still lists only `flight_number` — it is
       on the wire and the driver portal already renders it. The fallback keeps the card
       alive for a trip that carries a number but no tracking record yet. */
    const flight = trip.flight || (trip.flight_number ? {flight_number: trip.flight_number} : null)
    /* Nothing has been looked up yet: the record exists, tracking is on, and the backend
       has not polled the provider. Every field on it is null until it does. */
    const flightPending = Boolean(flight) && !flight.last_polled_at && !flight.flight_status
    // Driver card: assigned drives the caption, revealed drives the real details.
    const driverAssigned = isDriverAssigned(trip)
    const driverRevealed = isDriverRevealed(trip)

    /**
     * The driver's credentials line — "5 years of driving experience · 2,000 km on the road".
     *
     * ── ⚠️ EACH FIGURE CARRIES ITS OWN WORDS ─────────────────────────────────────
     * The two halves are written as complete phrases rather than as bare labels joined by a
     * separator, because either one can be the ONLY thing on the line. "2,000 Km" alone
     * reads as a fragment; "2,000 km on the road" is a sentence whichever half survives.
     *
     * ⚠️ NO "Airport experience" SUFFIX. Nothing in the payload says a driver's years or
     * distance were airport work — that was copy inherited from the placeholder, and
     * printing it would be the UI asserting something the backend never claimed.
     *
     * ⚠️ EVERY PART IS OPTIONAL, and an absent one is DROPPED rather than zeroed. Both
     * fields are genuinely null on a new driver (`experience_years: null`,
     * `total_distance_km` starting at 0), and "0 years of driving experience" is worse than
     * silence: it reads as a driver with none rather than as a figure not yet recorded.
     * With neither, the whole line is omitted.
     *
     * `Number(x) > 0` is the test on purpose — it collapses null, undefined, '' and 0 into
     * the same "nothing to say". The only value it rejects that is technically real is 0,
     * and 0 is precisely what must not be printed.
     */
    const driverStats = trip.driver?.stats
    const driverYears = Number(trip.driver?.experience_years)
    const driverKm = Number(driverStats?.total_distance_km)
    const driverCredentials = [
        // "1 year", not "1 years" — a singular that reads wrong undermines the figure.
        driverYears > 0 && `${driverYears} ${driverYears === 1 ? 'year' : 'years'} of driving experience`,
        driverKm > 0 && `${driverKm.toLocaleString()} km on the road`,
    ].filter(Boolean).join(' · ')
    // Why the Edit button is unavailable, or null when it's fine. Doubles as the
    // disabled flag and the hover-tooltip text, so the two can never disagree.
    // Payment first: it's the blocker the passenger can actually clear.
    //
    // Out of modifications: usage has caught up to the granted allowance. `free === used`
    // (a plain equality, not `>=`) so a trip whose usage exactly matches its quota is
    // blocked here too. Guarded on a real edit_policy and a non-Draft status so brand-new
    // /Draft trips (0 of 0, still mid confirm-flow → /confirm) and unrestricted trips
    // (no edit_policy) stay editable. NaN guard: a missing count (summary block) yields
    // NaN !== NaN, so absent data never falsely blocks.
    const ep = trip?.edit_policy
    const outOfModifications = !!ep && trip.status !== 'Draft'
        && Number(ep.free_modifications) === Number(ep.modifications_used)
    const editBlockedReason = paymentNotAuthorized
        ? 'First fix your payment issue'
        : outOfModifications
        ? 'Due to the system schedule, further modification is not allowed.'
        : null

    // When editing IS allowed, hovering warns how few changes are left — same copy the
    // edit page shows over its submit button, so the passenger is told BEFORE they start.
    const changesLeft = Math.max(
        0,
        (Number(trip?.edit_policy?.free_modifications) || 0) - (Number(trip?.edit_policy?.modifications_used) || 0)
    )
    const editWarning = !editBlockedReason && changesLeft > 0
        ? `There is only ${changesLeft} change and you can't change more than once, so please check before proceeding.`
        : null
    // Rendered next to the Modification tab's edit pencil — the only edit entry point
    // left on this page (the trip-info bar's button now opens the read-only /view page).
    const editTooltip = editBlockedReason
        ? <BlockedTooltip>{editBlockedReason}</BlockedTooltip>
        : editWarning ? <BlockedTooltip tone="info">{editWarning}</BlockedTooltip> : null

    return (
        <div className="flex flex-col gap-5 max-md:gap-3">
            <div className="grid grid-cols-12 gap-5 max-md:gap-3">
                {/* Left Column */}
                <div className="col-span-12 lg:col-span-9 flex flex-col gap-5 max-md:gap-3">
                    <RideTabNav activeCategory={activeCategory}/>

                    {/* Your Next Ride */}
                    <div className="relative white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                        <AlertMessage paymentHold={paymentHold}/>
                        <div className="grid grid-cols-12 gap-5 mb-15 max-md:mb-4 max-md:gap-3">
                            <div className="col-span-12 md:col-span-7">
                                {/* Header: desktop = title | status+button on one row.
                                    Mobile (<md) = status+button row on top, title below (Image #3):
                                    status moves up, title drops under it, button shrinks. */}
                                <div className="flex items-center justify-between gap-2 mb-2 max-md:flex-col max-md:items-stretch">
                                    <div className="flex items-center gap-2 max-md:order-2">
                                        <img src={carIcon} alt="car" className="max-md:h-6 max-md:w-6 object-contain"/>
                                        <h5 className="text-lg font-semibold mb-0! max-md:text-base!">Your Next Ride</h5>
                                    </div>
                                    <div className="flex items-center gap-2 max-md:order-1 max-md:justify-between">
                                        <span className={`${getStatusStyle(trip.status)} px-6 py-2.5 rounded-full max-md:px-3 max-md:py-1 max-md:text-xs max-md:font-semibold`}>{getStatusLabel(trip.status_label, trip.status)}</span>
                                        {isCancelled ? (
                                            // Cancelled trip → no "return" of a dead trip; offer a fresh booking.
                                            <button type="button" onClick={() => navigate('/book-ride')}
                                                    className="cursor-pointer flex items-center gap-2 py-2.5 pr-4 pl-2 bg-(--text-primary) text-white rounded-full transition-colors hover:opacity-80 max-md:gap-1.5 max-md:py-1.5 max-md:pr-3 max-md:pl-1.5 max-md:text-xs">
                                                <img alt="" src={plusIconLight} className="w-7 max-md:w-5"/>
                                                Book Ride
                                            </button>
                                        ) : !trip.parent_trip && (
                                            <span className="relative group">
                                                <button type="button" onClick={() => navigate(`/return-booking/${rideId}`)}
                                                        disabled={paymentNotAuthorized || hasReturnBooking}
                                                        className="cursor-pointer flex items-center gap-2 py-2.5 pr-4 pl-2 bg-(--text-primary) text-white rounded-full transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50 max-md:gap-1.5 max-md:py-1.5 max-md:pr-3 max-md:pl-1.5 max-md:text-xs">
                                                    <img alt="" src={plusIconLight} className="w-7 max-md:w-5"/>
                                                    Return Booking
                                                </button>
                                                {(hasReturnBooking || paymentNotAuthorized) && (
                                                    <span className="hidden group-hover:block absolute top-full right-0 mt-2 bg-[#3c393c] text-white rounded-lg px-3 py-2 text-xs text-center w-max max-w-[220px] z-30 after:content-[''] after:absolute after:bottom-full after:right-5 after:border-[6px] after:border-transparent after:border-b-[#3c393c]">{hasReturnBooking ? `This trip already has a return booking: ${trip.return_booking.id}` : 'First fix your payment issue'}</span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="mb-4 text-(--text-secondary) max-md:mb-3 max-md:text-xs">
                                    <p className="mb-0!">Trip {trip.reference || `#${rideId}`} &middot; <PickupTime trip={trip} format={formatDateTime} inline/></p>
                                </div>
                                <div className="relative max-md:text-sm">
                                    <div className="relative overflow-hidden">
                                        <span className="absolute h-full w-px left-3 top-5 border-l-2 border-dashed border-gray-900"></span>

                                        {/* Origin */}
                                        <div className="flex relative mb-6 min-h-7 max-md:mb-4 max-md:flex-col before:absolute before:content-[''] before:w-2 before:h-2 before:bg-gray-900 before:rounded-full before:outline-2 before:outline-gray-900 before:outline-offset-5 before:top-2 before:left-2 max-md:before:h-1.5 max-md:before:w-1.5 max-md:before:outline-offset-3 max-md:before:left-[9px] max-md:before:top-[9px]">
                                            <div className="w-1/2 pl-10 max-md:w-full max-md:pl-7"><PickupTime trip={trip} format={formatTime}/></div>
                                            <div className="w-1/2 max-md:mt-0.5 max-md:w-full max-md:pl-7">
                                                <span>{locationLabel(trip.origin)}</span>
                                            </div>
                                        </div>

                                        {/* Stopovers */}
                                        {trip.stop_overs?.length > 0 && trip.stop_overs.map((stop, i) => {
                                            const leg = legEnds[i]
                                            return (
                                                <div key={i} className="flex relative mb-6 min-h-7 max-md:mb-4 max-md:flex-col">
                                                    <img src={whiteCar} alt="car" className="absolute w-6 h-8 top-0.5 left-0 z-10 object-contain max-md:left-1 max-md:h-6 max-md:w-4"/>
                                                    <div className="w-1/2 pl-10 max-md:w-full max-md:pl-7">
                                                        {leg && <span className="block">{formatTime(new Date(leg.time))}</span>}
                                                        {leg?.durationMin > 0 && <span className="block text-gray-400 text-xs">{formatTravelTime(leg.durationMin)}</span>}
                                                    </div>
                                                    <div className="w-1/2 max-md:mt-0.5 max-md:w-full max-md:pl-7">
                                                        <span className="block">{locationLabel(stop.location || stop)}</span>
                                                    </div>
                                                </div>
                                            )
                                        })}

                                        {/* Destination */}
                                        <div className="flex relative mb-0 min-h-7 max-md:flex-col before:absolute before:content-[''] before:bg-white before:w-5 before:h-full before:z-10">
                                            <img src={locationIcon} alt="destination" className="absolute w-6 h-6 top-0 left-0 z-20 max-md:left-1 max-md:top-0.5 max-md:h-4 max-md:w-4"/>
                                            <div className="w-1/2 pl-10 max-md:w-full max-md:pl-7">
                                                <span className="block">{estimatedArrival}</span>
                                                {lastLegMin > 0 && <span className="block text-gray-400 text-xs">{formatTravelTime(lastLegMin)}</span>}
                                            </div>
                                            <div className="w-1/2 max-md:mt-0.5 max-md:w-full max-md:pl-7">
                                                <span className="block">{locationLabel(trip.destination)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-12 md:col-span-5 max-md:min-h-[200px]">
                                <TripRouteMap
                                    polyline={polyline}
                                    origin={typeof trip.origin === 'object' ? trip.origin : null}
                                    destination={typeof trip.destination === 'object' ? trip.destination : null}
                                    stopOvers={trip.stop_overs}
                                    legs={trip.route?.legs}
                                    summary={trip.route?.summary}
                                    mapUrl={trip.route?.map_url}
                                    className="h-full max-md:min-h-[200px]"
                                />
                            </div>
                        </div>

                        {/* Trip Info Bar */}
                        <div className={`grid grid-cols-12 gap-3 items-end ${dimmed}`}>
                            <div className="col-span-12 md:col-span-7">
                                <div className="flex justify-between bg-(--bg-grey) p-3 rounded-2xl max-md:p-2.5">
                                    <div className="flex">
                                        <div className="flex flex-col items-start">
                                            <span className="bg-(--text-primary) text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center -mb-5 z-9 max-md:w-4 max-md:h-4 max-md:text-[9px] max-md:-mb-2">{passengerCount}</span>
                                            <span className="w-25 h-25 bg-white border-6 border-(--bg-dark-grey) rounded-full flex items-center justify-center max-md:w-12 max-md:h-12 max-md:border-[3px]">
                                                <img src={personCarryingBag} alt="passengers" className="max-md:w-6 max-md:h-6 object-contain"/>
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-start -ml-3 max-md:-ml-2">
                                            <span className="bg-(--text-primary) text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center -mb-5 z-9 max-md:w-4 max-md:h-4 max-md:text-[9px] max-md:-mb-2">{childSeatCount}</span>
                                            <span className="w-25 h-25 bg-white border-6 border-(--bg-dark-grey) rounded-full flex items-center justify-center max-md:w-12 max-md:h-12 max-md:border-[3px]">
                                                <img src={childCarIcon} alt="child seat" className="max-md:w-6 max-md:h-6 object-contain"/>
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-start -ml-3 max-md:-ml-2">
                                            <span className="bg-(--text-primary) text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center -mb-5 z-9 max-md:w-4 max-md:h-4 max-md:text-[9px] max-md:-mb-2">{luggageCount}</span>
                                            <span className="w-25 h-25 bg-white border-6 border-(--bg-dark-grey) rounded-full flex items-center justify-center max-md:w-12 max-md:h-12 max-md:border-[3px]">
                                                <img src={luggageIcon} alt="luggage" className="max-md:w-6 max-md:h-6 object-contain"/>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <h5 className="font-bold mb-10! max-md:mb-4! max-md:text-sm!">Trip info</h5>
                                        {/* VIEW, not edit — this opens the read-only /view page, so it
                                            carries no edit gating: a cancelled or out-of-modifications
                                            trip can still be read. Editing lives on the Modification tab. */}
                                        <button onClick={() => navigate(`/rides/${rideId}/view`)} aria-label="View trip details"
                                                className="cursor-pointer w-12 h-12 rounded-full bg-(--text-primary) flex items-center justify-center border-0 max-md:h-9 max-md:w-9">
                                            <img src={arrowRightWhite} alt="" className="w-5 max-md:w-4"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="col-span-12 lg:col-span-3 flex flex-col gap-5 max-md:gap-3">
                    {/* Total + Pricing Breakdown */}
                    <div className={`white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color) ${dimmed}`}>
                        <Popover className="relative group">
                            <PopoverButton className="cursor-pointer w-full flex items-center justify-between bg-transparent text-left outline-none">
                                <h2 className="font-bold mb-0! max-md:text-xl!">Total {formatCents(totalPrice)}</h2>
                                <svg className="w-10 h-10 max-md:h-7 max-md:w-7 shrink-0 transition-transform duration-200 group-data-[open]:rotate-180" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                                </svg>
                            </PopoverButton>
                            <PopoverPanel anchor="bottom start" transition className="w-[var(--button-width)] mt-2 rounded-2xl bg-white border border-(--border-color) shadow-lg p-5 z-[999] transition duration-200 ease-out origin-top data-[closed]:opacity-0 data-[closed]:scale-95">
                                <div className="flex flex-col gap-3">
                                    {pricingBreakdown?.length > 0 ? (
                                        <>
                                            {pricingBreakdown.filter(item => item.value && item.value !== 0).map((item, i) => (
                                                <div key={i} className="flex justify-between">
                                                    <span className="text-(--text-secondary)">{item.label}</span>
                                                    <span className={`font-medium ${item.type === 'discount' ? 'text-(--text-green)' : ''}`}>
                                                        {item.type === 'discount' ? `-${formatCents(Math.abs(item.value))}` : formatCents(item.value)}
                                                    </span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between border-t border-(--border-color) pt-3">
                                                <span className="font-bold">Total</span>
                                                <span className="font-bold">{formatCents(totalPrice)}</span>
                                            </div>
                                        </>
                                    ) : pricing ? (
                                        <>
                                            {pricing.base_fare > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Base fare</span><span className="font-medium">{formatCents(pricing.base_fare)}</span></div>}
                                            {pricing.distance_fare > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Distance fare</span><span className="font-medium">{formatCents(pricing.distance_fare)}</span></div>}
                                            {pricing.vehicle_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Vehicle charge</span><span className="font-medium">{formatCents(pricing.vehicle_charge)}</span></div>}
                                            {pricing.child_seat_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Child seat</span><span className="font-medium">{formatCents(pricing.child_seat_charge)}</span></div>}
                                            {pricing.luggage_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Luggage</span><span className="font-medium">{formatCents(pricing.luggage_charge)}</span></div>}
                                            {pricing.stopover_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Stopover</span><span className="font-medium">{formatCents(pricing.stopover_charge)}</span></div>}
                                            {pricing.airport_fee > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Airport fee</span><span className="font-medium">{formatCents(pricing.airport_fee)}</span></div>}
                                            {pricing.surge_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Surge</span><span className="font-medium">{formatCents(pricing.surge_charge)}</span></div>}
                                            {pricing.tax > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Tax</span><span className="font-medium">{formatCents(pricing.tax)}</span></div>}
                                            {pricing.gratuity > 0 && (() => {
                                                const gratuityPct = pricing.gratuity_percentage ?? trip.selected_vehicle?.gratuity_percentage
                                                return <div className="flex justify-between"><span className="text-(--text-secondary)">Gratuity{gratuityPct != null ? ` (${gratuityPct}%)` : ''}</span><span className="font-medium">{formatCents(pricing.gratuity)}</span></div>
                                            })()}
                                            {pricing.online_discount > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Online discount</span><span className="font-medium text-(--text-green)">-{formatCents(pricing.online_discount)}</span></div>}
                                            {pricing.discount_code_discount > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Promo ({pricing.discount_code})</span><span className="font-medium text-(--text-green)">-{formatCents(pricing.discount_code_discount)}</span></div>}
                                            <div className="flex justify-between border-t border-(--border-color) pt-3">
                                                <span className="font-bold">Total</span>
                                                <span className="font-bold">{formatCents(pricing.total)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-(--text-secondary) mb-0!">No pricing details available</p>
                                    )}
                                </div>
                            </PopoverPanel>
                        </Popover>
                        {promoSaved > 0 && (
                            <span className="bg-(--bg-grey) text-center rounded-xs font-bold w-full block mt-3 py-2">You've saved {formatCents(promoSaved)} with a promo</span>
                        )}
                    </div>

                    {/* Payment Methods */}
                    <div className={`white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color) ${dimmed}`}>
                        <div className="flex items-center gap-2 mb-10 max-md:mb-4">
                            <img src={cardIcon} alt="Card" className="max-md:w-6"/>
                            <h4 className="mb-0! max-md:text-base!">Payment Methods</h4>
                        </div>
                        {hasCard || hasWallet ? (
                            <>
                                <div className="flex flex-wrap gap-3 mb-10 max-md:mb-4 max-md:gap-2">
                                    {hasCard && (
                                        <div className="bg-(--accent-green-light) text-(--text-primary) rounded-full px-10 py-2.5 inline-flex items-center gap-2 max-md:px-5 max-md:py-1.5 max-md:text-sm">
                                            <CardBrand brand={pmBrand} className="h-4"/>
                                            **** {pmLast4 || paymentMethod?.display_name || '****'}
                                        </div>
                                    )}
                                    {hasWallet && (
                                        <div className="bg-(--accent-green-light) text-(--text-primary) rounded-full px-10 py-2.5 inline-flex items-center gap-2 max-md:px-5 max-md:py-1.5 max-md:text-sm">
                                            Credit: {formatCents(trip.wallet_amount_cents)}
                                        </div>
                                    )}
                                </div>
                                {hasCard && isCancelled ? (
                                    <div className="flex items-center gap-2 text-lg mb-3 max-md:text-sm">
                                        <img alt="cancelled" src={cancelIcon} className="mr-2"/>
                                        <span>No charges have been made to your card</span>
                                    </div>
                                ) : hasCard && isCaptured === true ? (
                                    <div className="flex items-center gap-2 text-lg mb-3 max-md:text-sm">
                                        <img alt="success" src={greenTick} className="mr-2"/>
                                        <span>Captured successfully</span>
                                    </div>
                                ) : hasCard && isCaptured === false ? (
                                    <div className="flex items-center gap-2 text-lg mb-3 max-md:text-sm">
                                        <img alt="failed" src={cancelIcon} className="mr-2"/>
                                        <span>Credit card payment failed, retry or add new card</span>
                                    </div>
                                ) : hasCard && isAuthorized === true ? (
                                    <div className="flex items-center gap-2 text-lg mb-3 max-md:w-full max-md:text-sm">
                                        <img alt="success" src={greenTick} className="mr-2 max-md:mr-0 max-md:w-5"/>
                                        <span>Pre-authorized<br className="max-md:hidden"/> hold placed</span>
                                    </div>
                                ) : hasCard && isAuthorized === false ? (
                                    <div className="flex items-center gap-2 text-lg mb-3 max-md:w-full max-md:text-sm">
                                        <img alt="failed" src={cancelIcon} className="mr-2 max-md:mr-0 max-md:w-5"/>
                                        <span>Credit card payment failed, retry or add new card</span>
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <p className="text-(--text-secondary) mb-0!">No payment method selected</p>
                        )}

                        {(isAuthorized === false || isCaptured === false) && <RetryHoldButtons trip={trip}/>}
                    </div>

                    {/* Flight — under Payment Methods, and only on a trip that HAS one.

                        ⚠️ A CITY-TO-CITY RIDE GETS NO CARD AT ALL. An empty "Flight" panel on
                        a trip with no flight is worse than the space it would fill, which is
                        the same rule the driver portal's copy of this card follows.

                        ⚠️ THE TWO DELAY NUMBERS ARE DIFFERENT THINGS and both are shown.
                        `delay_minutes` is how late the PLANE is; `pickup_shift_total_minutes`
                        is how much of that the backend has already pushed the PICKUP by. A
                        passenger reading one as the other either turns up early for a car
                        that moved or waits for one that did not. */}
                    {flight && (
                        <div className={`white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color) ${dimmed}`}>
                            <div className="flex items-center gap-2 mb-10 max-md:mb-4">
                                <img src={planeArrival} alt="Flight" className="max-md:w-6"/>
                                <h4 className="mb-0! max-md:text-base!">Flight</h4>
                            </div>

                            <div className="bg-(--accent-green-light) text-(--text-primary) rounded-full px-10 py-2.5 inline-flex items-center gap-2 mb-10 max-md:mb-4 max-md:px-5 max-md:py-1.5 max-md:text-sm">
                                {flight.flight_number || trip.flight_number}
                            </div>

                            <div className="flex flex-col gap-3">
                                <FlightRow label="Status" value={flight.flight_status}/>
                                <FlightRow
                                    label="Flight delayed by"
                                    value={flight.delay_minutes > 0 ? formatTravelTime(flight.delay_minutes) : null}
                                />
                                <FlightRow
                                    label="Pickup moved by"
                                    value={flight.pickup_shift_total_minutes > 0
                                        ? formatTravelTime(flight.pickup_shift_total_minutes)
                                        : null}
                                />
                                <FlightRow
                                    label="Expected landing"
                                    value={flight.expected_landing_at ? formatDateTime(flight.expected_landing_at) : null}
                                />
                                <FlightRow label="Terminal" value={trip.airport_terminal?.name}/>
                                {/* Why tracking stopped, when it has. A cancelled or diverted
                                    flight says so here rather than silently freezing the
                                    status above on a value that will never change again. */}
                                <FlightRow label="Tracking stopped" value={flight.stopped_reason}/>
                            </div>

                            {/* ⚠️ THE CARD IS EMPTY UNTIL THE FIRST POLL, and saying so is the
                                point. Between booking and the backend's first look at the
                                flight, every field above is null — a blank card then reads as
                                a feature that is broken rather than one that has not started. */}
                            {flightPending && (
                                <p className="text-(--text-secondary) mb-0!">
                                    We&apos;re tracking this flight. Delays and any change to your
                                    pickup time will show here.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Full Width Bottom - Driver / Cancellation Reason or Policies.

                ⚠️ WHICH CARD IS TALLER FLIPS AT EnRoute, so the alignment has to flip with it.

                items-start (before EnRoute): each card sizes to its OWN content. The policies
                card is the tall one — three tabs of tiers and notes — and the grid's default
                stretch padded the short driver card out to match it.

                items-stretch (EnRoute onward): the policies card collapses to a single line,
                so it becomes the SHORT one and the same argument now runs the other way. Left
                on items-start it sat as a stub beside a full-height driver panel. */}
            <div className={`grid grid-cols-12 gap-5 max-md:gap-3 ${isTripUnderway(trip) ? 'items-stretch' : 'items-start'}`}>
                {/* Two stacked sections: the driver area (real details once revealed,
                    otherwise the blurred placeholder) and, BELOW it, the Track Driver
                    button — which is shown in every state, not only once the trip is
                    under way. */}
                <div className={`col-span-12 md:col-span-6 white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color) ${dimmed}`}>
                    {driverRevealed ? (
                        <div className="flex items-center justify-between gap-5 max-md:gap-3">
                            {/* Driver */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-end gap-4 mb-7 max-md:items-center max-md:gap-2.5 max-md:mb-3">
                                    <img src={trip.driver.photo} alt={trip.driver.name}
                                         className="w-[70px] h-[70px] rounded-full object-cover shrink-0 bg-(--bg-grey) max-md:h-14 max-md:w-14"/>
                                    <p className="font-bold text-2xl mb-0! truncate min-w-0 max-md:text-base max-md:whitespace-normal max-md:overflow-visible max-md:break-words">
                                        {trip.driver.name}
                                        {trip.driver.total_ratings > 0 && (
                                            <span className="text-(--text-secondary) font-medium"> ({Number(trip.driver.average_rating).toFixed(1)})</span>
                                        )}
                                    </p>
                                </div>
                                {/* The credentials line — see `driverCredentials`. Rendered
                                    only when there is something to say; the name row above
                                    carries its own mb-7, so dropping this leaves the spacing
                                    to the badge intact rather than collapsing it. */}
                                {driverCredentials && (
                                    <p className="text-(--text-secondary) text-xl mb-7 leading-snug max-md:mb-3 max-md:text-sm">
                                        {driverCredentials}
                                    </p>
                                )}
                                <img src={identityVarified} alt="Identity verified" className="h-9 w-auto max-md:h-6"/>
                            </div>
                            {/* Vehicle */}
                            {trip.driver.vehicle && (
                                <div className="shrink-0 flex flex-col items-center gap-7 max-w-[48%] max-md:gap-3 max-md:max-w-[42%]">
                                    {trip.driver.vehicle.image && (
                                        <img src={trip.driver.vehicle.image} alt="" className="w-[250px] max-w-full h-auto object-contain"/>
                                    )}
                                    <div className="bg-(--bg-grey) rounded-4xl py-2 px-5 text-center max-md:px-3 max-md:py-1.5 max-md:rounded-2xl">
                                        <p className="font-bold text-2xl mb-0! leading-tight max-md:text-base">{trip.driver.vehicle.plate_no}</p>
                                        <p className="text-(--text-secondary) text-xl font-bold mb-0! max-md:text-xs">{[trip.driver.vehicle.make, trip.driver.vehicle.model].filter(Boolean).join(' ')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Driver not on the way yet → dummy card behind a white blur, never
                           the real driver (privacy — see isDriverRevealed). The caption is
                           the ONLY part that changes on assignment: before one exists it
                           says WHEN the details arrive rather than that nothing has
                           happened — "Driver not assigned yet" reported an absence, which
                           reads as a problem on a booking the passenger has just paid for.
                           Once a driver exists we lead with the good news instead, then
                           explain that WHO they are unlocks when they set off (EnRoute). The two sentences are separate lines because the first
                           is the answer and the second is the caveat — run together in one
                           bold sentence, the caveat was what the passenger read first. */
                        <div className="relative overflow-hidden rounded-2xl">
                            <img src={driverPlaceHolder} alt="" className="block w-full h-auto"/>
                            <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2 px-5 max-md:gap-1 max-md:bg-white/85 max-md:px-4">
                                <p className="mb-0! font-bold text-2xl max-md:text-sm max-md:text-(--text-primary) text-(--text-secondary) text-center">
                                    {driverAssigned
                                        ? 'Driver is assigned successfully'
                                        : 'Driver details will be shared once your ride starts'}
                                </p>
                                {driverAssigned && (
                                    <p className="mb-0! text-base max-md:text-xs text-(--text-secondary) text-center">
                                        Their details will be shown once they are on the way.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ⚠️ THE CONTACT PAIR AND TRACK DRIVER SHARE A ROW, at opposite ends.
                        This page showed the driver's face, their car and their plate and gave
                        a passenger no way to reach them — the call and message buttons existed
                        only on the tracking screen, so someone who arrived here from an email
                        or the rides list had to find that screen first.

                        ⚠️ AND THEY ARE THE SAME CONTROLS, not a second pair. One call bridge,
                        one unread badge, one chat thread — see `DriverContactButtons`.

                        ⚠️ THEY APPEAR ONLY WITH THE REVEALED DRIVER, unlike Track Driver
                        beside them, which is shown in every state. There is nobody to call
                        until a driver is committed to the ride, and the blurred placeholder
                        above says exactly that. */}
                    <div className="mt-7 flex items-center gap-3 max-md:mt-3 max-md:gap-2">
                        {driverRevealed && <DriverContactButtons trip={trip}/>}

                        {/* ONGOING ride → straight to the web tracking page. Any other ride keeps
                            the Track Driver button's own behaviour: the download-the-app popup on
                            a desktop, the app hand-off on a phone. "Ongoing" is the ride lists'
                            own rule (`categoryForStatus`), so the two cannot disagree. */}
                        <div className="ml-auto flex items-center gap-3 max-md:gap-2">
                            <DriverArrivedBadge trip={trip}/>
                            {activeCategory === 'ongoing' ? (
                                <button
                                    type="button"
                                    onClick={() => navigate(`/${rideId}/track-driver`)}
                                    className="cursor-pointer flex items-center gap-2 font-medium text-sm bg-(--bg-grey) rounded-full px-5 py-2.5 text-(--text-primary) border-0 max-md:px-4 max-md:py-2 max-md:text-xs"
                                >
                                    Track Driver
                                    <img src={rightArrow} alt="next"/>
                                </button>
                            ) : (
                                <TrackDriverButton
                                    tripId={rideId}
                                    className="cursor-pointer flex items-center gap-2 font-medium text-sm bg-(--bg-grey) rounded-full px-5 py-2.5 text-(--text-primary) border-0 max-md:px-4 max-md:py-2 max-md:text-xs"
                                >
                                    Track Driver
                                    <img src={rightArrow} alt="next"/>
                                </TrackDriverButton>
                            )}
                        </div>
                    </div>
                </div>

                {/* Cancellation Reason (cancelled) OR Cancellation & Modify policies (upcoming) */}
                <div className="col-span-12 md:col-span-6 white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color) flex flex-col">
                    {isCancelled ? (
                        <>
                            <h5 className="font-bold mb-4 max-md:mb-2! max-md:text-base!">Cancellation Reason</h5>
                            <div className="bg-(--bg-grey) rounded-lg px-4 py-2.5 mb-5 max-md:mb-3 max-md:px-3 max-md:py-2 max-md:text-sm">
                                <span className="text-(--text-primary)">{trip.cancellation_reason || '—'}</span>
                            </div>
                        </>
                    ) : (
                        /* The tabbed Cancellation / Modification / Refund view, fed by THIS
                           trip's own tiers (cancellation_policy + edit_policy on the GET-trip
                           response) rather than the booking-flow /calculate-policies cache.
                           Cancel and Edit moved inside their matching tab. */
                        <CancellationModificationRefundContent
                            policy={tripCmrPolicy(trip)}
                            // EnRoute onward: the three tabs collapse to one line each.
                            // ⚠️ Copy only — the Cancel button below is still gated by
                            // `canCancel` (InProgress), so during EnRoute the panel says
                            // cancellation no longer applies while the control stays live.
                            // See the note on isTripUnderway in utils/functions.js.
                            underway={isTripUnderway(trip)}

                            tabActions={{
                                cancellation: trip.status !== 'Draft' ? (
                                    <CancelRide trip={trip} disabled={!canCancel} blockedReason={cancelBlockedReason}
                                                className="cursor-pointer px-6 py-2 rounded-full bg-(--bg-grey) flex items-center justify-center gap-2 text-(--text-red) font-bold border-0 disabled:opacity-50 disabled:cursor-not-allowed max-md:px-4 max-md:py-1.5 max-md:text-sm">
                                        <img alt="cancel" src={closeRed}/> Cancel
                                    </CancelRide>
                                ) : null,
                                modification: (
                                    <span className="relative group">
                                        <button onClick={() => navigate(`/rides/${rideId}/${trip.status === 'Draft' ? 'confirm' : 'edit'}`)} disabled={editBlockedReason !== null} className="cursor-pointer w-12 h-12 rounded-full bg-(--text-primary) flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed max-md:h-9 max-md:w-9">
                                            <img src={pencilIconBig} alt="edit"/>
                                        </button>
                                        {editTooltip}
                                    </span>
                                ),
                            }}
                        />
                    )}
                    <button type="button" onClick={() => setShowPolicies(true)} className="cursor-pointer flex items-center gap-2 font-medium text-sm bg-(--bg-grey) rounded-full px-5 py-2.5 ml-auto text-(--text-primary) mt-5 max-md:mt-3 max-md:px-4 max-md:py-2 max-md:text-xs">
                        More Policies
                        <img src={rightArrow} alt="next"/>
                    </button>
                </div>
            </div>

            <PoliciesPopup isOpen={showPolicies} onClose={() => setShowPolicies(false)} trip={trip}/>
        </div>
    )
}
